/**
 * Centered modal panel with a backdrop. Closes on Escape or backdrop click and
 * locks body scroll while open. Renders nothing when `open` is false.
 */
import React, { useEffect } from 'react';

export default function SlidePanel({ open, onClose, title, children, width = 660 }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div style={styles.backdrop} onClick={onClose} />
      <div style={{ ...styles.modal, width, maxWidth: '92vw' }}>
        <div style={styles.header}>
          <h2 style={styles.title}>{title}</h2>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">&#x2715;</button>
        </div>
        <div style={styles.body}>{children}</div>
      </div>
    </>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(33, 81, 45, 0.35)',
    zIndex: 200,
    animation: 'fadeIn 0.15s ease',
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '88vh',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 24px 64px rgba(33,81,45,0.18), 0 4px 16px rgba(0,0,0,0.08)',
    zIndex: 201,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'popIn 0.18s ease',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 28px 16px',
    borderBottom: '1px solid #f0f0f0',
    flexShrink: 0,
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 18,
    fontWeight: 800,
    color: 'var(--color-forest)',
    margin: 0,
  },
  closeBtn: {
    fontSize: 16,
    color: 'var(--color-forest)',
    opacity: 0.45,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    lineHeight: 1,
    padding: 6,
    borderRadius: '50%',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 28px 28px',
  },
};
